/**
 * SkimBuster subscription API (same contract as skimbuster-mobile SubscriptionService).
 * CRA: set REACT_APP_STRIPE_WORKER_URL to override the default worker host.
 */

import { auth } from '../firebase/config';
import type { SubscriptionPlan } from '../constants/Subscriptions';
import type { SubscriptionSku } from '../constants/stripePrices';

const DEFAULT_WORKER_BASE = 'https://stripe-worker.micclo.workers.dev';

const REGISTER_PATH_CHECKOUT = '/subscription/register-checkout';
const REGISTER_PATH_FINALIZE = '/subscription/register-finalize';
/** Validate a worker-issued enterprise code at registration (no Firebase session yet). */
const REDEEM_ENTERPRISE_REGISTRATION_PATH = '/subscription/redeem-enterprise-code';
/** Validate code when an existing company upgrades to Enterprise (requires Bearer token). */
const REDEEM_ENTERPRISE_UPGRADE_PATH = '/subscription/redeem-enterprise-upgrade';
/**
 * After enterprise registration, Firebase `companyId` exists; worker should set Stripe Customer +
 * Subscription metadata (e.g. skimbuster_company_id, skimbuster_user_email) so they appear in Dashboard.
 */
const SYNC_ENTERPRISE_STRIPE_METADATA_PATH = '/subscription/sync-enterprise-stripe-metadata';

/** Worker sometimes returns price-catalog errors; enterprise redeem should only validate the code. */
function humanizeEnterpriseRedeemError(raw: string): string {
  const lower = raw.toLowerCase();
  if (
    lower.includes('no stripe price') ||
    lower.includes('stripe price') ||
    lower.includes('price found for enterprise') ||
    lower.includes('price for enterprise') ||
    lower.includes('no price') ||
    lower.includes('price is not configured') ||
    lower.includes('price id')
  ) {
    return (
      'This activation code could not be verified. If you believe the code is correct, contact SkimBuster support.'
    );
  }
  return raw;
}

function registrationWorkerHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const key = process.env.REACT_APP_STRIPE_REGISTER_KEY?.trim();
  if (key) headers['X-Skimbuster-Registration-Key'] = key;
  return headers;
}

async function registrationWorkerFetch(path: string, body: Record<string, unknown>): Promise<Response> {
  const base = getStripeWorkerBase();
  return fetch(`${base}${path}`, {
    method: 'POST',
    headers: registrationWorkerHeaders(),
    body: JSON.stringify(body),
  });
}

/**
 * Sent to POST /subscription/register-checkout (no Firebase session).
 * Worker should: store pending registration, create Stripe Checkout **mode: subscription** only
 * (do **not** create a subscription in a prior step), then on checkout.session.completed create
 * Firebase Auth user + Firestore companies/users docs and link Stripe customer to companyId.
 */
export type RegistrationCheckoutPayload = {
  email: string;
  password: string;
  companyName: string;
  title: string;
  firstName: string;
  lastName: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  phoneNumber: string;
  plan: SubscriptionPlan;
  isAnnual: boolean;
  priceId: string;
  planSku: SubscriptionSku;
  category: 'corporate' | 'law';
  successUrl: string;
  cancelUrl: string;
};

/**
 * Start paid registration: payment before Firebase account. Requires worker route register-checkout.
 */
export async function createRegistrationCheckout(
  payload: RegistrationCheckoutPayload
): Promise<{ checkoutUrl: string }> {
  const res = await registrationWorkerFetch(REGISTER_PATH_CHECKOUT, payload as unknown as Record<string, unknown>);
  const text = await res.text();
  if (!res.ok) {
    let err: { error?: string } = {};
    try {
      err = text ? JSON.parse(text) : {};
    } catch {
      err = { error: text || `Request failed: ${res.status}` };
    }
    if (res.status === 404) {
      const base = getStripeWorkerBase();
      throw new Error(
        `Stripe worker has no registration checkout route (404). Deploy a worker that handles POST ${REGISTER_PATH_CHECKOUT}, ` +
          `or set REACT_APP_STRIPE_WORKER_URL in .env to the correct deployment. Current base: ${base}`
      );
    }
    throw new Error(
      err.error ||
        `Registration checkout is not available (${res.status}). Ensure the Stripe worker implements POST ${REGISTER_PATH_CHECKOUT}.`
    );
  }
  try {
    const data = (text ? JSON.parse(text) : {}) as { checkoutUrl?: string };
    if (!data.checkoutUrl) throw new Error('No checkout URL returned');
    return { checkoutUrl: data.checkoutUrl };
  } catch (e) {
    if (e instanceof Error && e.message === 'No checkout URL returned') throw e;
    throw new Error('Invalid response from registration checkout.');
  }
}

/**
 * Optional: ask worker to finalize Firebase user from a paid Checkout session (if webhook is slow).
 * Safe to ignore 404.
 */
export async function registerFinalizeAfterCheckout(sessionId: string): Promise<boolean> {
  const res = await registrationWorkerFetch(REGISTER_PATH_FINALIZE, { sessionId });
  if (res.status === 404) return false;
  if (!res.ok) return false;
  return true;
}

/**
 * Worker contract: POST /subscription/redeem-enterprise-code (no auth).
 * Validates a single-use or limited enterprise code; may create/link Stripe subscription server-side.
 * Returns optional `subscription` blob to store on companies/{uid}.subscription (Stripe linkage).
 *
 * **Stripe metadata (implement in worker):** On Customer and Subscription, set at least
 * `skimbuster_user_email` (same as `email`), `skimbuster_plan_id`, `skimbuster_category`.
 * `skimbuster_company_id` is only known after Firebase signup — client calls
 * {@link syncEnterpriseStripeMetadataForCompany} next, or worker can patch metadata when that runs.
 */
export type RedeemEnterpriseRegistrationPayload = {
  enterpriseCode: string;
  /** Billing / login email — show on Stripe Customer and metadata. */
  email: string;
  planId: string;
  category: 'corporate' | 'law';
  /** Helps Stripe Customer `name` / description in Dashboard. */
  companyName: string;
  firstName?: string;
  lastName?: string;
};

export type RedeemEnterpriseResult = {
  /** When true, client should proceed without Checkout. */
  ok: boolean;
  /** Merge into Firestore companies.{companyId}.subscription when present. */
  subscription?: CompanySubscriptionFromFirestore | null;
};

function parseRedeemEnterpriseResponse(text: string): RedeemEnterpriseResult {
  const data = (text ? JSON.parse(text) : {}) as Record<string, unknown>;
  const ok = data.ok === true || data.valid === true || data.success === true;
  let subscription: CompanySubscriptionFromFirestore | null = null;
  const sub = data.subscription;
  if (sub && typeof sub === 'object' && sub !== null) {
    subscription = sub as CompanySubscriptionFromFirestore;
  } else if (data.stripeCustomerId && typeof data.stripeCustomerId === 'string') {
    subscription = {
      stripeCustomerId: data.stripeCustomerId as string,
      stripeSubscriptionId: (data.stripeSubscriptionId as string) || '',
      status: (data.status as SubscriptionStatus) || 'active',
      plan: (data.plan as string) || 'enterprise',
      priceId: (data.priceId as string) || '',
      trialEnd: (data.trialEnd as number | null) ?? null,
      currentPeriodEnd: typeof data.currentPeriodEnd === 'number' ? data.currentPeriodEnd : 0,
      cancelAtPeriodEnd: Boolean(data.cancelAtPeriodEnd),
      category: data.category as 'law' | 'corporate' | undefined,
      billingInterval: data.billingInterval as 'month' | 'year' | undefined,
      unitAmountCents: typeof data.unitAmountCents === 'number' ? data.unitAmountCents : undefined,
    };
  }
  return { ok, subscription };
}

export async function redeemEnterpriseRegistrationCode(
  payload: RedeemEnterpriseRegistrationPayload
): Promise<RedeemEnterpriseResult> {
  const res = await registrationWorkerFetch(REDEEM_ENTERPRISE_REGISTRATION_PATH, {
    enterpriseCode: payload.enterpriseCode.trim(),
    email: payload.email.trim(),
    planId: payload.planId,
    category: payload.category,
    companyName: payload.companyName.trim(),
    ...(payload.firstName != null && payload.firstName !== '' ? { firstName: payload.firstName.trim() } : {}),
    ...(payload.lastName != null && payload.lastName !== '' ? { lastName: payload.lastName.trim() } : {}),
  });
  const text = await res.text();
  if (!res.ok) {
    let err: { error?: string } = {};
    try {
      err = text ? JSON.parse(text) : {};
    } catch {
      err = { error: text || `Request failed: ${res.status}` };
    }
    if (res.status === 404) {
      throw new Error(
        `Enterprise activation is not available (404). Implement POST ${REDEEM_ENTERPRISE_REGISTRATION_PATH} on the subscription service.`
      );
    }
    throw new Error(
      humanizeEnterpriseRedeemError(err.error || `Invalid or expired activation code (${res.status}).`)
    );
  }
  try {
    return parseRedeemEnterpriseResponse(text);
  } catch {
    throw new Error('Invalid response from enterprise code redemption.');
  }
}

export type RedeemEnterpriseUpgradePayload = {
  enterpriseCode: string;
  /** Firestore / SkimBuster company id (same as masterOwnerID for admins). */
  companyId: string;
  /** Admin email — worker should set Stripe Customer/Subscription metadata for Dashboard search. */
  email: string;
  planId: string;
  category: 'corporate' | 'law';
};

/**
 * Worker contract: POST /subscription/redeem-enterprise-upgrade (Authorization: Bearer Firebase ID token).
 * **Stripe metadata:** Set `skimbuster_company_id`, `skimbuster_user_email` on Customer and active Subscription.
 */
export async function redeemEnterpriseUpgradeCode(
  payload: RedeemEnterpriseUpgradePayload
): Promise<RedeemEnterpriseResult> {
  const res = await workerFetch(REDEEM_ENTERPRISE_UPGRADE_PATH, {
    enterpriseCode: payload.enterpriseCode.trim(),
    companyId: payload.companyId,
    email: payload.email.trim(),
    planId: payload.planId,
    category: payload.category,
  });
  const text = await res.text();
  if (!res.ok) {
    let err: { error?: string } = {};
    try {
      err = text ? JSON.parse(text) : {};
    } catch {
      err = { error: text || `Request failed: ${res.status}` };
    }
    if (res.status === 404) {
      throw new Error(
        `Enterprise activation is not available (404). Implement POST ${REDEEM_ENTERPRISE_UPGRADE_PATH} on the subscription service.`
      );
    }
    throw new Error(
      humanizeEnterpriseRedeemError(err.error || `Invalid or expired activation code (${res.status}).`)
    );
  }
  try {
    return parseRedeemEnterpriseResponse(text);
  } catch {
    throw new Error('Invalid response from enterprise code redemption.');
  }
}

export type SyncEnterpriseStripeMetadataPayload = {
  stripeCustomerId: string;
  /** Firebase company document id (admin’s masterOwnerID). */
  companyId: string;
  userEmail: string;
  stripeSubscriptionId?: string;
  /**
   * When set, worker finalizes ENT code reserved at enterprise registration
   * (`__skimbuster_enterprise_pending__` → real companyId in Firestore).
   */
  enterpriseCode?: string;
};

/**
 * Worker contract: POST /subscription/sync-enterprise-stripe-metadata (Bearer: Firebase ID token).
 * Updates Stripe Customer and, if provided, Subscription metadata so Dashboard shows company + email.
 * Recommended metadata keys: `skimbuster_company_id`, `skimbuster_user_email`.
 * Returns 404 if not implemented — client treats as no-op.
 */
export async function syncEnterpriseStripeMetadataForCompany(
  payload: SyncEnterpriseStripeMetadataPayload
): Promise<boolean> {
  const body: Record<string, unknown> = {
    stripeCustomerId: payload.stripeCustomerId.trim(),
    companyId: payload.companyId.trim(),
    userEmail: payload.userEmail.trim(),
  };
  if (payload.stripeSubscriptionId?.trim()) {
    body.stripeSubscriptionId = payload.stripeSubscriptionId.trim();
  }
  if (payload.enterpriseCode?.trim()) {
    body.enterpriseCode = payload.enterpriseCode.trim();
  }
  const res = await workerFetch(SYNC_ENTERPRISE_STRIPE_METADATA_PATH, body);
  if (res.status === 404) return false;
  if (!res.ok) {
    const text = await res.text();
    console.warn('[stripeWorker] sync-enterprise-stripe-metadata failed:', res.status, text?.slice(0, 200));
    return false;
  }
  return true;
}

export function getStripeWorkerBase(): string {
  const fromEnv = process.env.REACT_APP_STRIPE_WORKER_URL?.trim();
  return (fromEnv && fromEnv.length > 0 ? fromEnv.replace(/\/$/, '') : DEFAULT_WORKER_BASE);
}

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid';

export interface CompanySubscriptionFromFirestore {
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  status: SubscriptionStatus;
  plan: string;
  priceId: string;
  trialEnd: number | null;
  currentPeriodEnd: number;
  cancelAtPeriodEnd: boolean;
  category?: 'law' | 'corporate';
  billingInterval?: 'month' | 'year';
  unitAmountCents?: number;
  /** Stripe line quantity when billing is per-seat (mirrors purchased seats). */
  quantity?: number;
}

async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in. Please sign in again, then try again.');
  const token = await user.getIdToken(true);
  if (!token) throw new Error('Failed to get ID token. Please sign in again.');
  return token;
}

async function workerFetch(path: string, body: Record<string, unknown>): Promise<Response> {
  const token = await getIdToken();
  const base = getStripeWorkerBase();
  return fetch(`${base}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
}

export async function createCustomer(
  email: string,
  companyId: string,
  planSku: string = 'basic',
  category: 'corporate' | 'law' = 'corporate'
): Promise<{
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  trialEnd: number | null;
  status: string;
  category?: 'law' | 'corporate';
  billingInterval?: 'month' | 'year';
  unitAmountCents?: number;
}> {
  let res: Response | undefined;
  let text = '';
  try {
    res = await workerFetch('/subscription/create-customer', {
      email,
      companyId,
      planSku,
      category,
    });
    text = await res.text();
    if (text && typeof text === 'string') {
      text = text.replace(/^\uFEFF/, '').trim();
    }
    if (!res.ok) {
      let err: { error?: string } = {};
      try {
        err = text ? JSON.parse(text) : {};
      } catch {
        err = { error: text || `Request failed: ${res.status}` };
      }
      throw new Error(err.error || `Request failed: ${res.status}`);
    }
    if (!text) throw new Error('Empty response');
    return JSON.parse(text) as {
      stripeCustomerId: string;
      stripeSubscriptionId: string;
      trialEnd: number | null;
      status: string;
      category?: 'law' | 'corporate';
      billingInterval?: 'month' | 'year';
      unitAmountCents?: number;
    };
  } catch (e) {
    if (res !== undefined) {
      const preview = text ? ` Body: ${text.slice(0, 200)}${text.length > 200 ? '…' : ''}` : ' (empty body)';
      throw new Error(`Invalid response from server (status: ${res.status}).${preview}`);
    }
    throw e;
  }
}

export type CreateCheckoutOptions = {
  successUrl?: string;
  cancelUrl?: string;
  /**
   * When `create-customer` already created a subscription (trialing/active), use `setup` so Checkout
   * only collects a payment method for that customer — not a second `subscription` Checkout (which
   * would duplicate subscriptions and invoices in the Stripe portal).
   * Your worker must map this to Stripe `mode: 'setup'` and, on `checkout.session.completed`, attach
   * the payment method to the customer / existing subscription as needed.
   */
  checkoutMode?: 'setup' | 'subscription';
};

export async function createCheckout(
  stripeCustomerId: string,
  priceId: string,
  companyId: string,
  options?: CreateCheckoutOptions
): Promise<{ checkoutUrl: string }> {
  const payload: Record<string, unknown> = {
    stripeCustomerId,
    priceId,
    companyId,
  };
  if (options?.successUrl) payload.successUrl = options.successUrl;
  if (options?.cancelUrl) payload.cancelUrl = options.cancelUrl;
  if (options?.checkoutMode) payload.checkoutMode = options.checkoutMode;

  const res = await workerFetch('/subscription/create-checkout', payload);
  const text = await res.text();
  if (!res.ok) {
    let err: { error?: string } = {};
    try {
      err = text ? JSON.parse(text) : {};
    } catch {
      err = { error: text || `Request failed: ${res.status}` };
    }
    throw new Error(err.error || `Request failed: ${res.status}`);
  }
  try {
    const data = (text ? JSON.parse(text) : {}) as { checkoutUrl?: string };
    if (!data.checkoutUrl) throw new Error('No checkout URL returned');
    return { checkoutUrl: data.checkoutUrl };
  } catch (e) {
    if (e instanceof Error && e.message !== 'No checkout URL returned') {
      throw new Error('Invalid response from server.');
    }
    throw e;
  }
}

export async function createPortal(
  stripeCustomerId: string,
  returnUrl?: string
): Promise<{ portalUrl: string }> {
  const payload: Record<string, unknown> = { stripeCustomerId };
  if (returnUrl) payload.returnUrl = returnUrl;

  const res = await workerFetch('/subscription/create-portal', payload);
  const text = await res.text();
  if (!res.ok) {
    let err: { error?: string } = {};
    try {
      err = text ? JSON.parse(text) : {};
    } catch {
      err = { error: text || `Request failed: ${res.status}` };
    }
    throw new Error(err.error || `Request failed: ${res.status}`);
  }
  try {
    const data = (text ? JSON.parse(text) : {}) as { portalUrl?: string };
    if (!data.portalUrl) throw new Error('No portal URL returned');
    return { portalUrl: data.portalUrl };
  } catch (e) {
    if (e instanceof Error && e.message !== 'No portal URL returned') {
      throw new Error('Invalid response from server.');
    }
    throw e;
  }
}

/** Normalize Firestore / worker value to Unix seconds (number or Timestamp-like). */
export function toUnixSeconds(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return value > 10_000_000_000 ? Math.floor(value / 1000) : value;
  if (
    typeof value === 'object' &&
    value !== null &&
    'seconds' in value &&
    typeof (value as { seconds: number }).seconds === 'number'
  ) {
    return (value as { seconds: number }).seconds;
  }
  return null;
}

export async function getStatus(params: {
  companyId?: string;
  stripeCustomerId?: string;
}): Promise<{
  status: SubscriptionStatus | null;
  subscription: CompanySubscriptionFromFirestore | null;
}> {
  const res = await workerFetch('/subscription/status', params as Record<string, unknown>);
  const text = await res.text();
  if (!res.ok) {
    let err: { error?: string } = {};
    try {
      err = text ? JSON.parse(text) : {};
    } catch {
      err = { error: text || `Request failed: ${res.status}` };
    }
    throw new Error(err.error || `Request failed: ${res.status}`);
  }
  try {
    const data = (text ? JSON.parse(text) : {}) as {
      status?: SubscriptionStatus | null;
      subscription?: CompanySubscriptionFromFirestore | null;
    };
    return {
      status: data.status ?? null,
      subscription: data.subscription ?? null,
    };
  } catch {
    throw new Error('Invalid response from server.');
  }
}
